import { useRef, useState } from 'react';
import { showToast } from '../components/Toast';
import { compressImage, MAX_UPLOAD_BYTES } from '../utils/imageCompress';

const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Pick, shrink, upload and remove one photo (a member's profile photo, an event's photo).
 *
 * Images are compressed in the browser first, as task screenshots and score photos are,
 * and anything still over 3 MB is turned away before it is sent. `upload(file)` and
 * `remove()` are the API calls; both resolve to a response whose data carries the new
 * photo_url (or null), which is handed to `onChange`. Render a hidden file input with
 * `inputProps` and call `pick()` to open it.
 */
export function usePhotoUpload({ upload, remove, onChange, fileName = 'photo.jpg' }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handlePick = async (event) => {
    const [file] = Array.from(event.target.files || []);
    event.target.value = '';
    if (!file) return;

    let prepared = file;
    try {
      prepared = await compressImage(file, fileName);
    } catch {
      // Compression is an optimisation, not a gate. Send the original and let the checks below decide.
    }
    if (!ACCEPTED_TYPES.includes(prepared.type)) {
      showToast('That file is not a JPEG, PNG or WebP image', 'error');
      return;
    }
    if (prepared.size > MAX_UPLOAD_BYTES) {
      showToast('That photo is over 3 MB even after shrinking', 'error');
      return;
    }

    setBusy(true);
    try {
      const response = await upload(prepared);
      showToast('Photo saved', 'success');
      onChange?.(response.data.photo_url);
    } catch (error) {
      showToast(error.response?.data?.error || 'Could not save the photo', 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    try {
      await remove();
      showToast('Photo removed', 'success');
      onChange?.(null);
    } catch (error) {
      showToast(error.response?.data?.error || 'Could not remove the photo', 'error');
    } finally {
      setBusy(false);
    }
  };

  return {
    busy,
    pick: () => inputRef.current?.click(),
    remove: handleRemove,
    inputProps: {
      ref: inputRef,
      type: 'file',
      accept: ACCEPTED_TYPES.join(','),
      hidden: true,
      onChange: handlePick
    }
  };
}
